import React, { useState, useEffect } from 'react';
import { meetAPI } from '../../services/api';
import './TeacherPages.css';

const Schedule = () => {
  const [bookings, setBookings] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await meetAPI.getBookings({});
      setBookings(response.data.bookings);
    } catch (error) {
      console.error('Bookings fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInstantMeet = async () => {
    try {
      const response = await meetAPI.createInstantMeet({ durationMinutes: 30 });
      window.open(response.data.meetLink, '_blank');
    } catch (error) {
      alert('Failed to create meeting: ' + error.message);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // 月の最初の曜日まで空白を追加
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // 月の日数を追加
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const getBookingsForDate = (date) => {
    if (!date) return [];
    return bookings.filter(b => {
      const bookingDate = new Date(b.scheduled_at);
      return bookingDate.toDateString() === date.toDateString();
    });
  };

  const isToday = (date) => {
    if (!date) return false;
    return date.toDateString() === new Date().toDateString();
  };

  const hasLessons = (date) => {
    return getBookingsForDate(date).length > 0;
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'var(--color-success)',
      pending: 'var(--color-warning)',
      completed: 'var(--color-info)',
      cancelled: 'var(--color-danger)'
    };
    return colors[status] || 'var(--color-text-light)';
  };

  const days = getDaysInMonth(currentDate);
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  if (loading) {
    return (
      <div className="flex-center" style={{ height: '50vh' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  const selectedDateBookings = selectedDate ? getBookingsForDate(selectedDate) : [];

  return (
    <div className="schedule-page fade-in">
      <div className="schedule-header">
        <div>
          <h1>📅 Schedule Management</h1>
          <p className="page-subtitle">スケジュール管理 - Manage your lessons</p>
        </div>
        <button className="btn btn-primary" onClick={handleInstantMeet}>
          📹 Start Instant Meet
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
        {/* カレンダー */}
        <div className="schedule-calendar">
          <div className="calendar-header">
            <h3>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h3>
            <div className="calendar-nav">
              <button className="btn btn-outline btn-sm" onClick={prevMonth}>←</button>
              <button className="btn btn-outline btn-sm" onClick={nextMonth}>→</button>
            </div>
          </div>

          <div className="calendar-grid">
            {dayNames.map(day => (
              <div key={day} className="calendar-day-header">{day}</div>
            ))}
            
            {days.map((date, index) => (
              <div
                key={index}
                className={`calendar-day ${date ? '' : 'empty'} ${isToday(date) ? 'today' : ''} ${hasLessons(date) ? 'has-lessons' : ''}`}
                onClick={() => date && setSelectedDate(date)}
                style={{
                  opacity: date ? 1 : 0,
                  cursor: date ? 'pointer' : 'default',
                  border: selectedDate && date && selectedDate.toDateString() === date.toDateString() 
                    ? '2px solid var(--color-primary)' 
                    : 'none'
                }}
              >
                {date && (
                  <span className="calendar-day-number">{date.getDate()}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 選択した日のレッスン */}
        <div className="schedule-calendar">
          <h3 style={{ marginBottom: '16px' }}>
            {selectedDate 
              ? selectedDate.toLocaleDateString('ja-JP', { month: 'long', day: 'numeric', weekday: 'long' })
              : 'Select a date'}
          </h3>
          
          {selectedDate ? (
            selectedDateBookings.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {selectedDateBookings.map(booking => (
                  <div 
                    key={booking.id}
                    style={{
                      padding: '16px',
                      background: 'var(--color-bg)',
                      borderRadius: 'var(--radius)',
                      borderLeft: `4px solid ${getStatusColor(booking.status)}`
                    }}
                  >
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                      {formatTime(booking.scheduled_at)} - {booking.student_name}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--color-text-light)', marginBottom: '8px' }}>
                      {booking.lesson_title || 'Japanese Lesson'}
                    </div>
                    {booking.google_meet_link && booking.status === 'confirmed' && (
                      <a 
                        href={booking.google_meet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm"
                      >
                        Join Meet
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: 'var(--color-text-light)', textAlign: 'center', padding: '32px' }}>
                No lessons scheduled<br />
                予定なし
              </p>
            )
          ) : (
            <p style={{ color: 'var(--color-text-light)', textAlign: 'center', padding: '32px' }}>
              Click on a date to see lessons<br />
              日付をクリックしてレッスンを表示
            </p>
          )}
        </div>
      </div>

      {/* 今週のレッスン一覧 */}
      <div style={{ marginTop: '24px' }}>
        <h2 style={{ marginBottom: '16px' }}>📋 All Upcoming Lessons / 今後のレッスン</h2>
        <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          {bookings
            .filter(b => new Date(b.scheduled_at) >= new Date() && b.status !== 'cancelled')
            .slice(0, 10)
            .map(booking => (
              <div 
                key={booking.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px 20px',
                  borderBottom: '1px solid var(--color-border)'
                }}
              >
                <div style={{ 
                  minWidth: '100px',
                  color: 'var(--color-primary)',
                  fontWeight: '600'
                }}>
                  {new Date(booking.scheduled_at).toLocaleDateString('ja-JP', {
                    month: 'short',
                    day: 'numeric'
                  })}
                  <br />
                  <span style={{ fontSize: '13px' }}>
                    {formatTime(booking.scheduled_at)}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <strong>{booking.student_name}</strong>
                  <span style={{ marginLeft: '8px', color: 'var(--color-text-light)' }}>
                    {booking.lesson_title || 'Japanese Lesson'}
                  </span>
                </div>
                <span 
                  className="badge"
                  style={{ background: getStatusColor(booking.status), color: 'white' }}
                >
                  {booking.status}
                </span>
                {booking.google_meet_link && booking.status === 'confirmed' && (
                  <a 
                    href={booking.google_meet_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-sm"
                  >
                    Join
                  </a>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Schedule;
